import { useState, useEffect } from 'react';

const MyGraph = ({ 
  width=200,
  height=200,
  strokeWidth=0.5, 
  color="#cccccc", 
  graphOpacity=0.8,
  lineOpacity=0.8,
  className="", 
  longitudeSteps=[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
  latitudeSteps=[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
  coordinates=[{x: 0, y: 3}, {x: 3, y: 7}, {x: 5, y: 2}, {x: 7, y: 9}],
  labelPrefix="",
  labelSuffix=""
}) => {
  const [animatedCoordinates, setAnimatedCoordinates] = useState(coordinates);
  const [largestX, setLargestX] = useState(Math.max(...coordinates.map(coord => coord.x)));
  const [smallestX, setSmallestX] = useState(Math.min(...coordinates.map(coord => coord.x)));
  const [largestY, setLargestY] = useState(Math.max(...coordinates.map(coord => coord.y)));
  const [smallestY, setSmallestY] = useState(Math.min(...coordinates.map(coord => coord.y)));

  useEffect(() => {
    const startCoords = animatedCoordinates;
    const endCoords = coordinates;
    const duration = 300; // 300ms animation
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function (ease-in-out)
      const easeInOut = progress < 0.5 
        ? 2 * progress * progress 
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;

      const interpolatedCoords = startCoords.map((startCoord, index) => {
        const endCoord = endCoords[index] || startCoord;
        return {
          x: startCoord.x + (endCoord.x - startCoord.x) * easeInOut,
          y: startCoord.y + (endCoord.y - startCoord.y) * easeInOut
        };
      });

      // Handle case where new coordinates array is longer
      if (endCoords.length > startCoords.length) {
        // Get the last point to start new points from, or use (0,0) if no points exist
        const lastPoint = startCoords.length > 0 ? startCoords[startCoords.length - 1] : { x: 0, y: 0 };
        
        for (let i = startCoords.length; i < endCoords.length; i++) {
          interpolatedCoords.push({
            x: lastPoint.x + (endCoords[i].x - lastPoint.x) * easeInOut,
            y: lastPoint.y + (endCoords[i].y - lastPoint.y) * easeInOut
          });
        }
      } else if (endCoords.length < startCoords.length) {
        for (let i = endCoords.length; i < startCoords.length; i++) {
          interpolatedCoords.pop();
        }
      }

      setAnimatedCoordinates(interpolatedCoords);
      setLargestX(Math.max(...interpolatedCoords.map(coord => coord.x)));
      setSmallestX(Math.min(...interpolatedCoords.map(coord => coord.x)));
      setLargestY(Math.max(...interpolatedCoords.map(coord => coord.y)));
      setSmallestY(Math.min(...interpolatedCoords.map(coord => coord.y)));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    animate();
  }, [coordinates]);

  return (
  <div style={{ width: width, height: height }}>
    <svg 
      width="100%"
      height="100%"
      viewBox="0 0 240 120" 
      fill="none"
      className={className}
    >
      {/* Longitude lines (vertical) */}
      <g>
        {longitudeSteps.map((step, index) => {
          const x = 10 + (index / (longitudeSteps.length - 1)) * 220;
          return (
            <line
              key={`longitude-${index}-${step}`}
              x1={x}
              y1={10}
              x2={x}
              y2={110}
              stroke={color}
              strokeWidth={strokeWidth}
              opacity={index === 0 || index === longitudeSteps.length - 1 ? graphOpacity : graphOpacity*0.6}
            />
          );
        })}
      </g>
      
      {/* Latitude lines (horizontal) */}
      <g>
        {latitudeSteps.map((step, index) => {
          const y = 10 + (index / (latitudeSteps.length - 1)) * 100;
          return (
            <line
              key={`latitude-${index}-${step}`}
              x1={10}
              y1={y}
              x2={230}
              y2={y}
              stroke={color}
              strokeWidth={strokeWidth}
              opacity={index === 0 || index === latitudeSteps.length - 1 ? graphOpacity : graphOpacity*0.6}
            />
          );
        })}
      </g>
      
      
      {/* Longitude labels (bottom) */}
      <g>
        {longitudeSteps.map((step, index) => {
          const x = 10 + (index / (longitudeSteps.length - 1)) * 220;
          const labelValue = (smallestX + (largestX - smallestX) / 10 * step).toFixed(0);
          const fontSize = Math.max(2, Math.min(5, 12 / labelValue.length));
          return (
            <text
              key={`longitude-label-${index}-${step}`}
              x={x}
              y={118}
              textAnchor="middle"
              fontSize={fontSize}
              fill={color}
              opacity={graphOpacity*0.8}
            >
              {labelValue}
            </text>
          );
        })}
      </g>
      
      {/* Latitude labels (left) */}
      <g>
        {latitudeSteps.map((step, index) => {
          const y = 110 - (index / (latitudeSteps.length - 1)) * 100;
          const labelValue = (smallestY + (largestY - smallestY) / 10 * step).toFixed(0);
          const fontSize = Math.max(2, Math.min(5, 12 / (labelValue.length+1)));
          return (
            <text
              key={`latitude-label-${index}-${step}`}
              x={4}
              y={y + 1}
              textAnchor="middle"
              fontSize={fontSize}
              fill={color}
              opacity={graphOpacity*0.8}
            >
              {labelPrefix}{labelValue}{labelSuffix}
            </text>
          );
        })}
      </g>

      {/* Line connecting coordinate points */}
      <g>
        {animatedCoordinates.length > 1 && (
          <polyline
            points={animatedCoordinates
              .map(coord => {
                const svgX = 10 + ((coord.x - smallestX) / (largestX - smallestX)) * 220;
                const svgY = 110 - ((coord.y - smallestY) / (largestY - smallestY)) * 100;
                return `${svgX},${svgY}`;
              })
              .join(' ')}
            fill="none"
            stroke="#ff4444"
            strokeWidth={strokeWidth * 2}
            opacity={lineOpacity}
          />
        )}
      </g>

      {/* Center cross lines for emphasis */}
      <line
        x1={10}
        y1={10}
        x2={10}
        y2={110}
        stroke={color}
        strokeWidth={strokeWidth * 1.5}
        opacity={graphOpacity}
      />
      <line
        x1={10}
        y1={110}
        x2={230}
        y2={110}
        stroke={color}
        strokeWidth={strokeWidth * 1.5}
        opacity={graphOpacity}
      />
    </svg>
  </div>
);
};

export default MyGraph;